import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Edit, Trash2, Star, Eye, EyeOff } from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { SubscriptionPlanDialog } from "./SubscriptionPlanDialog";
import { DeletePlanDialog } from "./DeletePlanDialog";
import {
  getAllSubscriptionPlans,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  togglePlanRecommendation,
  togglePlanActive,
  SubscriptionPlanDB,
} from "@/services/subscriptionPlanService";

export function SubscriptionPlansManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanDB | null>(null);
  const queryClient = useQueryClient();

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: getAllSubscriptionPlans,
  });

  const createMutation = useMutation({
    mutationFn: createSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSubscriptionPlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });

  const toggleRecommendationMutation = useMutation({
    mutationFn: ({ id, isRecommended }: { id: string; isRecommended: boolean }) =>
      togglePlanRecommendation(id, isRecommended),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      togglePlanActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
  });

  const handleSave = async (data: any) => {
    if (data.id) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = async () => {
    if (selectedPlan) {
      await deleteMutation.mutateAsync(selectedPlan.id);
      setDeleteDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  const handleEdit = (plan: SubscriptionPlanDB) => {
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  const handleDeleteClick = (plan: SubscriptionPlanDB) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  const handleCreateNew = () => {
    setSelectedPlan(null);
    setDialogOpen(true);
  };

  const filteredPlans = plans.filter((plan) =>
    plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    plan.tier.toLowerCase().includes(searchQuery.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      accessorKey: "name",
      header: "Plan Name",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.original.name}</span>
          {row.original.is_recommended && (
            <Badge variant="secondary" className="gap-1">
              <Star className="h-3 w-3" />
              Recommended
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: "tier",
      header: "Tier",
      cell: ({ row }: any) => (
        <Badge variant="outline" className="capitalize">
          {row.original.tier}
        </Badge>
      ),
    },
    {
      accessorKey: "price_display",
      header: "Price",
      cell: ({ row }: any) => (
        <span className="font-semibold">{row.original.price_display}</span>
      ),
    },
    {
      accessorKey: "lead_limit",
      header: "Lead Limit",
      cell: ({ row }: any) => (
        <span>
          {row.original.lead_limit > 900000000 ? "Unlimited" : row.original.lead_limit}
        </span>
      ),
    },
    {
      accessorKey: "features",
      header: "Features",
      cell: ({ row }: any) => {
        const features = row.original.features || [];
        const displayedFeatures = features.slice(0, 2);
        const remainingCount = features.length - 2;
        
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px] sm:max-w-xs">
            {displayedFeatures.map((feature: string, idx: number) => (
              <Badge key={idx} variant="secondary" className="text-xs truncate max-w-full">
                {feature}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Badge variant="outline" className="text-xs flex-shrink-0">
                +{remainingCount}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }: any) => (
        <Badge variant={row.original.is_active ? "default" : "secondary"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const plan = row.original;
        
        return (
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                toggleRecommendationMutation.mutate({
                  id: plan.id,
                  isRecommended: !plan.is_recommended,
                })
              }
              title={plan.is_recommended ? "Remove recommendation" : "Mark as recommended"}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              <Star
                className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${plan.is_recommended ? "fill-yellow-400 text-yellow-400" : ""}`}
              />
              <span className="sr-only">Toggle recommendation</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                toggleActiveMutation.mutate({
                  id: plan.id,
                  isActive: !plan.is_active,
                })
              }
              title={plan.is_active ? "Deactivate" : "Activate"}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              {plan.is_active ? (
                <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              ) : (
                <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              )}
              <span className="sr-only">Toggle active</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(plan)}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="sr-only">Edit</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteClick(plan)}
              disabled={!plan.is_active}
              className="h-8 w-8 p-0 sm:h-9 sm:w-9"
            >
              <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-destructive" />
              <span className="sr-only">Delete</span>
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold">Subscription Plans</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage available subscription plans and their features
            </p>
          </div>
          <Button 
            onClick={handleCreateNew}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Plan
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground flex-shrink-0" />
          <Input
            placeholder="Search plans..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10"
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            {searchQuery ? "No plans found matching your search." : "No subscription plans found. Create your first plan to get started."}
          </div>
        ) : (
          <div className="w-full overflow-hidden">
            <DataTable columns={columns} data={filteredPlans} />
          </div>
        )}
      </div>

      <SubscriptionPlanDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plan={selectedPlan}
        onSave={handleSave}
      />

      <DeletePlanDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        planName={selectedPlan?.name || ""}
      />
    </>
  );
}
